import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { TIDYCAL_URL } from "@/config/links";

const LINKS = [
  { label: "Accueil", anchor: "#accueil" },
  { label: "Diagnostic Business", anchor: "#diagnostic" },
  { label: "Orientation Formation", anchor: "#orientation" },
  { label: "La métamorphose", anchor: "#metamorphose" },
  { label: "Pourquoi OKAÏA Dév", anchor: "#pourquoi" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("accueil");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (location.pathname !== "/") return;
    const ids = LINKS.map((l) => l.anchor.slice(1));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px" }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [location.pathname]);

  const goTo = (anchor) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const el = document.querySelector(anchor);
        if (el) window.__lenis?.scrollTo(el, { offset: -80 });
      }, 300);
    } else {
      const el = document.querySelector(anchor);
      if (el) window.__lenis?.scrollTo(el, { offset: -80 });
    }
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-[#0B2215]/95 backdrop-blur-md border-b border-[#C8A97E]/20" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 md:px-12">
        <Link to="/" data-testid="navbar-logo" className="font-serif-display text-xl tracking-tight">
          <span className="font-semibold text-[#C8A97E]">OKAÏA</span> <span className="italic text-[#F9F8F6]">Dév</span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Navigation principale">
          {LINKS.map((l) => {
            const isActive = location.pathname === "/" && active === l.anchor.slice(1);
            return (
              <a
                key={l.anchor}
                href={l.anchor}
                onClick={goTo(l.anchor)}
                data-testid={`nav-link-${l.anchor.slice(1)}`}
                aria-current={isActive ? "true" : undefined}
                className={`relative pb-1 text-xs font-medium uppercase tracking-[0.18em] transition-colors duration-300 hover:text-[#C8A97E] ${
                  isActive ? "text-[#C8A97E]" : "text-[#F9F8F6]/75"
                }`}
              >
                {l.label}
                <span
                  className={`absolute -bottom-0.5 left-0 h-px bg-[#C8A97E] transition-[width] duration-500 ${
                    isActive ? "w-full" : "w-0"
                  }`}
                />
              </a>
            );
          })}
          <a
            href={TIDYCAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-cta"
            className="group inline-flex items-center gap-2 border border-[#C8A97E] bg-[#C8A97E] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-[#0B2215] transition-colors duration-300 hover:bg-transparent hover:text-[#C8A97E]"
          >
            Réserver mon diagnostic — 49 €
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="text-[#F9F8F6] lg:hidden"
          aria-label="Menu"
          aria-expanded={open}
          aria-controls="mobile-menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            id="mobile-menu"
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-[#C8A97E]/20 bg-[#0B2215] lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l) => {
                const isActive = location.pathname === "/" && active === l.anchor.slice(1);
                return (
                  <a
                    key={l.anchor}
                    href={l.anchor}
                    onClick={goTo(l.anchor)}
                    className={`flex items-center gap-3 py-3 font-serif-display text-2xl transition-colors duration-300 hover:text-[#C8A97E] ${
                      isActive ? "text-[#C8A97E]" : "text-[#F9F8F6]"
                    }`}
                  >
                    {isActive && <span className="inline-block h-px w-6 bg-[#C8A97E]" />}
                    {l.label}
                  </a>
                );
              })}
              <a
                href={TIDYCAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-cta"
                className="mt-4 inline-flex w-fit items-center gap-2 bg-[#C8A97E] px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#0B2215]"
              >
                Réserver mon diagnostic — 49 € <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
